import { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function warnings(o) {
  const w = [];
  if (o.payment_status === "paid" || o.payment_intent_id || o.payment_session_id)
    w.push(`Has a Stripe payment record${o.payment_status === "paid" ? ` (paid ${money(o.total)})` : ""}. Deleting does NOT refund or cancel it in Stripe.`);
  if (o.tracking?.number || o.label_url)
    w.push(`Has shipping/tracking${o.tracking?.number ? ` (${o.tracking.carrier || ""} ${o.tracking.number})` : ""}. Deleting does NOT cancel or refund the Shippo label.`);
  if (o.status === "Pending Payment") w.push("Pending payment: any stock reserved for it is NOT returned automatically.");
  if (o.custom_request_number) w.push(`Linked to custom request ${o.custom_request_number}, which stays unchanged.`);
  return w;
}

export function DeleteOrderButton({ order, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const w = warnings(order);

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Order ${order.order_number} deleted`);
      onDeleted();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`delete-order-${order.order_number}`} aria-label="Delete order" disabled={busy}
          className="flex h-10 w-10 items-center justify-center rounded-full text-kys-charcoal transition-colors hover:bg-red-50 hover:text-destructive"><Trash2 className="h-5 w-5" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="delete-order-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete this order?</AlertDialogTitle>
          <AlertDialogDescription>
            {order.order_number} · {order.customer?.email} · {order.status}. This removes only the website's order record and can't be undone. No emails are sent and inventory isn't changed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {w.length > 0 && (
          <ul data-testid="delete-order-warnings" className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {w.map((t) => <li key={t} className="flex gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t}</li>)}
          </ul>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-order-cancel">Keep order</AlertDialogCancel>
          <AlertDialogAction data-testid="delete-order-confirm" onClick={del} className="bg-destructive text-white hover:bg-destructive/90">Delete order</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
